import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth uses httpOnly cookies — every request must carry credentials
axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

const formatApiError = (error) => {
  if (!error.response) return 'Network error. Please try again.';
  const detail = error.response.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d.message).join(', ');
  return 'An error occurred. Please try again.';
};

const NO_REFRESH_URLS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On 401, try a cookie-based refresh once, then retry the original request
  useEffect(() => {
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !NO_REFRESH_URLS.some(u => originalRequest.url?.includes(u))
        ) {
          originalRequest._retry = true;
          try {
            await axios.post(`${API_URL}/api/auth/refresh`, {});
            return axios(originalRequest);
          } catch {
            setUser(false);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, data);
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const googleLogin = () => {
    // Emergent-managed Google OAuth — canonical pattern (per integration playbook).
    // The redirect URL MUST stay dynamic via window.location.origin so it works in
    // preview, production, and custom domains. The auth host itself IS the Emergent
    // service domain and is intentionally hardcoded.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google/callback`, { session_id: sessionId });
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, googleLogin, updateUser, checkAuth, handleGoogleCallback,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isSeller: user?.role === 'seller' || user?.role === 'both' || user?.role === 'admin',
      isBuyer: user?.role === 'buyer' || user?.role === 'both' || user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
